import React, { useState, useEffect } from 'react';
import { researchStatusLabel } from '../utils/researchStatus';
import { currentLocale, targetingLabel } from '../i18n';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import ResearchAIAnalysisPanel from './ResearchAIAnalysisPanel';
import ResearchDashboardDievo from './ResearchDashboardDievo';
import { describeTargeting, ResearchTargeting } from '../utils/researchTargeting';
import { getBranding } from '../config/branding';
import '../styles/research-dashboard.css';

interface ResearchData {
  id: number;
  name: string;
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED';
  respondent_count: number;
  target_respondents?: number;
  current_respondents: number;
  progress_percent?: number;
  questions: QuestionResult[];
  demographics: {
    age: { range: string; count: number; percentage: number }[];
    gender: { label: string; count: number; percentage: number }[];
    geo: { region: string; count: number; percentage: number }[];
  };
  progress?: {
    launched_at: string;
    original_launched_at: string;
    duration_days_min: number;
    duration_days_max: number;
    is_complete: boolean;
  };
  targeting?: ResearchTargeting;
}

interface QuestionResult {
  id: number;
  position: number;
  question_text: string;
  question_type: string;
  answers: AnswerResult[];
  demographic_breakdown: DemographicBreakdown[];
}

interface AnswerResult {
  id: number;
  answer_text: string;
  answer_image?: string | null;   // creative_test: option image URL
  count: number;
  percentage: number;
}

// Absolute URL for an uploaded option image (served by the API under /uploads).
const imgUrl = (u?: string | null) => u ? `${(api.defaults.baseURL || '').replace(/\/$/, '')}${u}` : '';

interface DemographicBreakdown {
  group: string; // e.g., "M 20-24", "F 25-34"
  answers: { answer_text: string; percentage: number }[];
}

interface ResearchDashboardProps {
  research: any;
  onPause: () => void;
  onResume: () => void;
  saving: boolean;
}

// Answer color palette (10 brand-cohesive colors)
const ANSWER_COLORS = [
  '#FF6B4A', // Brand orange - anchor
  '#6B3FA0', // Deep violet - purple family
  '#FFAB4A', // Warm amber - golden
  '#E94B6C', // Brand coral - anchor
  '#4A4A6A', // Sophisticated slate
  '#FF8C69', // Soft salmon
  '#8E3A9A', // Brand purple - anchor
  '#D4956A', // Warm caramel
  '#C74B8C', // Magenta rose
  '#7A5C8A', // Dusty plum
];

// Age colors (contrasting palette)
const AGE_COLORS = ['#FF6B4A', '#FBBF24', '#22C55E', '#3B82F6', '#A855F7', '#EC4899'];

// Gender colors (matching existing design)
const GENDER_COLORS = ['#E94B6C', '#9B4DA8'];

// Geo colors (location-themed palette)
const GEO_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// Donut chart component (moved outside to prevent re-renders)
interface DonutChartProps {
  data: { label: string; value: number; percentage: number; color: string }[];
  size?: number;
  title: string;
  maxLegendItems?: number; // Limit legend items, group rest as "Others"
}

const DonutChart: React.FC<DonutChartProps> = ({ data, size = 200, title, maxLegendItems }) => {
  const { t } = useTranslation();
  // Prepare legend data (optionally limit items)
  const legendData = maxLegendItems && data.length > maxLegendItems
    ? [
        ...data.slice(0, maxLegendItems),
        {
          label: t('research.others'),
          value: data.slice(maxLegendItems).reduce((sum, item) => sum + item.value, 0),
          percentage: data.slice(maxLegendItems).reduce((sum, item) => sum + item.percentage, 0),
          color: '#9CA3AF', // gray
        },
      ]
    : data;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const center = size / 2;
  const outerRadius = size / 2 - 10;
  const innerRadius = outerRadius * 0.5;
  const labelRadius = (outerRadius + innerRadius) / 2;

  const polarToCartesian = (angle: number, radius: number) => {
    const radians = (angle * Math.PI) / 180;
    return {
      x: center + radius * Math.cos(radians),
      y: center + radius * Math.sin(radians),
    };
  };

  const createArcPath = (startAngle: number, endAngle: number) => {
    if (endAngle - startAngle >= 359.99) {
      // Full circle - draw two arcs
      return `M ${center} ${center - outerRadius}
              A ${outerRadius} ${outerRadius} 0 1 1 ${center} ${center + outerRadius}
              A ${outerRadius} ${outerRadius} 0 1 1 ${center} ${center - outerRadius}
              M ${center} ${center - innerRadius}
              A ${innerRadius} ${innerRadius} 0 1 0 ${center} ${center + innerRadius}
              A ${innerRadius} ${innerRadius} 0 1 0 ${center} ${center - innerRadius} Z`;
    }

    const startOuter = polarToCartesian(startAngle, outerRadius);
    const endOuter = polarToCartesian(endAngle, outerRadius);
    const startInner = polarToCartesian(endAngle, innerRadius);
    const endInner = polarToCartesian(startAngle, innerRadius);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${startOuter.x} ${startOuter.y}
            A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y}
            L ${startInner.x} ${startInner.y}
            A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${endInner.x} ${endInner.y}
            Z`;
  };

  // Calculate segments with angles for labels
  const segments: { label: string; value: number; startAngle: number; endAngle: number; percentage: number; color: string }[] = [];
  let angleCalc = -90;
  data.forEach((segment) => {
    const angle = (segment.percentage / 100) * 360;
    segments.push({
      label: segment.label,
      value: segment.value,
      startAngle: angleCalc,
      endAngle: angleCalc + angle,
      percentage: segment.percentage,
      color: segment.color,
    });
    angleCalc += angle;
  });

  const handleMouseMove = (e: React.MouseEvent, index: number) => {
    const rect = e.currentTarget.closest('.donut-chart-wrapper')?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top - 10,
      });
    }
    setHoveredIndex(index);
  };

  return (
    <div className="donut-chart-container">
      <h4 className="donut-chart-title">{title}</h4>
      <div className="donut-chart-wrapper">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {segments.map((segment, index) => {
            if (segment.percentage < 0.5) return null;

            return (
              <path
                key={index}
                d={createArcPath(segment.startAngle, segment.endAngle - 0.5)}
                fill={segment.color}
                className={`donut-segment ${hoveredIndex === index ? 'hovered' : ''}`}
                style={{ animationDelay: `${index * 0.08}s` }}
                onMouseMove={(e) => handleMouseMove(e, index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            );
          })}
          {/* Percentage labels */}
          {segments.map((segment, index) => {
            if (segment.percentage < 5) return null; // Hide labels for small segments

            const midAngle = (segment.startAngle + segment.endAngle) / 2;
            const labelPos = polarToCartesian(midAngle, labelRadius);

            return (
              <text
                key={`label-${index}`}
                x={labelPos.x}
                y={labelPos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="donut-label"
              >
                {segment.percentage.toFixed(0)}%
              </text>
            );
          })}
        </svg>
        {/* Tooltip */}
        {hoveredIndex !== null && segments[hoveredIndex] && (
          <div
            className="donut-tooltip"
            style={{
              left: tooltipPos.x,
              top: tooltipPos.y,
            }}
          >
            <div className="donut-tooltip-label">{segments[hoveredIndex].label}</div>
            <div className="donut-tooltip-value">{segments[hoveredIndex].value.toLocaleString(currentLocale())}</div>
            <div className="donut-tooltip-percent">{segments[hoveredIndex].percentage.toFixed(1)}%</div>
          </div>
        )}
      </div>
      <div className="donut-legend">
        {legendData.map((item, index) => (
          <div key={index} className="donut-legend-item">
            <span className="donut-legend-color" style={{ background: item.color }} />
            <span className="donut-legend-label">{item.label}</span>
            <span className="donut-legend-value">{item.value.toLocaleString(currentLocale())}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Pie chart for question answers (moved outside to prevent re-renders)
interface PieChartProps {
  data: { label: string; count: number; percentage: number; color: string; image?: string | null }[];
  size?: number;
}

const PieChart: React.FC<PieChartProps> = ({ data, size = 180 }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const center = size / 2;
  const outerRadius = size / 2 - 4;
  const innerRadius = outerRadius * 0.5;
  const labelRadius = (outerRadius + innerRadius) / 2;

  const polarToCartesian = (angle: number, r: number) => {
    const radians = (angle * Math.PI) / 180;
    return {
      x: center + r * Math.cos(radians),
      y: center + r * Math.sin(radians),
    };
  };

  const createArcPath = (startAngle: number, endAngle: number) => {
    if (endAngle - startAngle >= 359.99) {
      return `M ${center} ${center - outerRadius}
              A ${outerRadius} ${outerRadius} 0 1 1 ${center} ${center + outerRadius}
              A ${outerRadius} ${outerRadius} 0 1 1 ${center} ${center - outerRadius}
              M ${center} ${center - innerRadius}
              A ${innerRadius} ${innerRadius} 0 1 0 ${center} ${center + innerRadius}
              A ${innerRadius} ${innerRadius} 0 1 0 ${center} ${center - innerRadius} Z`;
    }

    const startOuter = polarToCartesian(startAngle, outerRadius);
    const endOuter = polarToCartesian(endAngle, outerRadius);
    const startInner = polarToCartesian(endAngle, innerRadius);
    const endInner = polarToCartesian(startAngle, innerRadius);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${startOuter.x} ${startOuter.y}
            A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y}
            L ${startInner.x} ${startInner.y}
            A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${endInner.x} ${endInner.y}
            Z`;
  };

  // Calculate segments with angles
  const segments: { label: string; count: number; startAngle: number; endAngle: number; percentage: number; color: string; image?: string | null }[] = [];
  let angleCalc = -90;
  data.forEach((segment) => {
    const angle = (segment.percentage / 100) * 360;
    segments.push({
      label: segment.label,
      count: segment.count,
      startAngle: angleCalc,
      endAngle: angleCalc + angle,
      percentage: segment.percentage,
      color: segment.color,
      image: segment.image,
    });
    angleCalc += angle;
  });

  const handleMouseMove = (e: React.MouseEvent, index: number) => {
    const rect = e.currentTarget.closest('.question-pie-wrapper')?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top - 10,
      });
    }
    setHoveredIndex(index);
  };

  return (
    <div className="question-pie-wrapper">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="pie-chart">
        {segments.map((segment, index) => {
          if (segment.percentage < 0.5) return null;

          return (
            <path
              key={index}
              d={createArcPath(segment.startAngle, segment.endAngle - 0.5)}
              fill={segment.color}
              className={`pie-segment ${hoveredIndex === index ? 'hovered' : ''}`}
              style={{ animationDelay: `${index * 0.08}s` }}
              onMouseMove={(e) => handleMouseMove(e, index)}
              onMouseLeave={() => setHoveredIndex(null)}
            />
          );
        })}
        {/* Percentage labels */}
        {segments.map((segment, index) => {
          if (segment.percentage < 8) return null;

          const midAngle = (segment.startAngle + segment.endAngle) / 2;
          const labelPos = polarToCartesian(midAngle, labelRadius);

          return (
            <text
              key={`label-${index}`}
              x={labelPos.x}
              y={labelPos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="pie-label"
            >
              {segment.percentage.toFixed(0)}%
            </text>
          );
        })}
      </svg>
      {/* Tooltip */}
      {hoveredIndex !== null && segments[hoveredIndex] && (
        <div
          className="donut-tooltip"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y,
          }}
        >
          {segments[hoveredIndex].image && (
            <img className="tooltip-thumb" src={imgUrl(segments[hoveredIndex].image)} alt="" />
          )}
          <div className="donut-tooltip-label">{segments[hoveredIndex].label}</div>
          <div className="donut-tooltip-value">{segments[hoveredIndex].count.toLocaleString(currentLocale())}</div>
          <div className="donut-tooltip-percent">{segments[hoveredIndex].percentage.toFixed(1)}%</div>
        </div>
      )}
    </div>
  );
};

// Horizontal bar chart for multiple_choice questions
interface HorizontalBarChartProps {
  data: { label: string; count: number; percentage: number; color: string }[];
}

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({ data }) => {
  const { t } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Find max percentage for scaling (cap at 100 for visual consistency)
  const maxPercentage = Math.max(...data.map(d => d.percentage), 100);

  const handleMouseMove = (e: React.MouseEvent, index: number) => {
    const rect = e.currentTarget.closest('.horizontal-bar-chart')?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top - 10,
      });
    }
    setHoveredIndex(index);
  };

  return (
    <div className="horizontal-bar-chart">
      {data.map((item, index) => (
        <div key={index} className="horizontal-bar-row">
          <div className="horizontal-bar-label" title={item.label}>
            {item.label}
          </div>
          <div className="horizontal-bar-track">
            <div
              className={`horizontal-bar-fill ${hoveredIndex === index ? 'hovered' : ''}`}
              style={{
                width: `${(item.percentage / maxPercentage) * 100}%`,
                backgroundColor: item.color,
              }}
              onMouseMove={(e) => handleMouseMove(e, index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <span className="horizontal-bar-value-inside">{item.percentage.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      ))}

      {hoveredIndex !== null && (
        <div
          className="horizontal-bar-tooltip"
          style={{ left: tooltipPos.x, top: tooltipPos.y }}
        >
          <div className="horizontal-bar-tooltip-label">{data[hoveredIndex].label}</div>
          <div className="horizontal-bar-tooltip-value">{t('research.respondentsCount', { count: data[hoveredIndex].count, value: data[hoveredIndex].count.toLocaleString(currentLocale()) })}</div>
          <div className="horizontal-bar-tooltip-percent">{data[hoveredIndex].percentage.toFixed(1)}%</div>
        </div>
      )}
    </div>
  );
};

// Stacked bar chart for demographic breakdown (moved outside to prevent re-renders)
interface StackedBarChartProps {
  breakdown: DemographicBreakdown[];
  answers: AnswerResult[];
}

// A 180px column: under ~10% of it a segment is thinner than the type.
const SEGMENT_LABEL_MIN_PCT = 10;

const StackedBarChart: React.FC<StackedBarChartProps> = ({ breakdown, answers }) => {
  const [hoveredSegment, setHoveredSegment] = useState<{ group: string; answer: string; percentage: number } | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const answerColors = answers.reduce((acc, answer, index) => {
    acc[answer.answer_text] = ANSWER_COLORS[index % ANSWER_COLORS.length];
    return acc;
  }, {} as Record<string, string>);

  const answerImages = answers.reduce((acc, a) => {
    if (a.answer_image) acc[a.answer_text] = a.answer_image;
    return acc;
  }, {} as Record<string, string>);

  const handleMouseMove = (e: React.MouseEvent, group: string, answerText: string, percentage: number) => {
    const rect = e.currentTarget.closest('.stacked-bar-chart')?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top - 10,
      });
    }
    setHoveredSegment({ group, answer: answerText, percentage });
  };

  return (
    <div className="stacked-bar-chart">
      <div className="stacked-bar-axis">
        <span>0%</span>
        <span>25%</span>
        <span>50%</span>
        <span>75%</span>
        <span>100%</span>
      </div>
      <div className="stacked-bars">
        {breakdown.map((group, groupIndex) => {
          // A slice's height is its share of the column, so the bar only reaches
          // the top when the values happen to add up to exactly 100. Single-choice
          // answers miss by a rounding tenth; a multiple-choice question is not a
          // composition at all and can land anywhere — 95.6% here, 105.8% there,
          // which read as bars that stop short or get clipped. Scale each column
          // by its own total so it always fills and the eye compares composition.
          const groupTotal = group.answers.reduce((sum, a) => sum + (a.percentage || 0), 0) || 100;
          return (
          <div key={groupIndex} className="stacked-bar-column">
            <div className="stacked-bar">
              {group.answers.map((answer, answerIndex) => {
                const shareOfColumn = (answer.percentage / groupTotal) * 100;
                return (
                <div
                  key={answerIndex}
                  className={`stacked-bar-segment ${hoveredSegment?.group === group.group && hoveredSegment?.answer === answer.answer_text ? 'hovered' : ''}`}
                  style={{
                    height: `${shareOfColumn}%`,
                    background: answerColors[answer.answer_text],
                    animationDelay: `${groupIndex * 0.05 + answerIndex * 0.02}s`,
                  }}
                  onMouseMove={(e) => handleMouseMove(e, group.group, answer.answer_text, answer.percentage)}
                  onMouseLeave={() => setHoveredSegment(null)}
                >
                  {/* the big slices carry their share on the bar itself, so the
                      split reads without hovering; thin ones stay tooltip-only */}
                  {shareOfColumn >= SEGMENT_LABEL_MIN_PCT && (
                    <span className="stacked-bar-value">{answer.percentage.toFixed(0)}%</span>
                  )}
                </div>
                );
              })}
            </div>
            <span className="stacked-bar-label">{group.group}</span>
          </div>
          );
        })}
      </div>
      {/* Tooltip */}
      {hoveredSegment && (
        <div
          className="stacked-bar-tooltip"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y,
          }}
        >
          <div className="stacked-bar-tooltip-group">{hoveredSegment.group}</div>
          {answerImages[hoveredSegment.answer] && (
            <img className="tooltip-thumb" src={imgUrl(answerImages[hoveredSegment.answer])} alt="" />
          )}
          <div className="stacked-bar-tooltip-answer">{hoveredSegment.answer}</div>
          <div className="stacked-bar-tooltip-percent">{hoveredSegment.percentage.toFixed(1)}%</div>
        </div>
      )}
    </div>
  );
};

const ResearchDashboard: React.FC<ResearchDashboardProps> = ({
  research,
  onPause,
  onResume,
  saving,
}) => {
  const { t } = useTranslation();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showAudience, setShowAudience] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null);
  const [results, setResults] = useState<ResearchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Fetch results from API. Results are generated in the background after launch, so the
  // endpoint may answer {generating:true} for a couple of minutes — keep polling SILENTLY
  // (the dashboard just shows its normal warming-up state) until the real results arrive.
  useEffect(() => {
    let cancelled = false;
    let timer: any = null;

    const fetchResults = async (isPoll = false) => {
      if (!isPoll) setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/api/sense/research/${research.id}/results`);
        if (cancelled) return;
        if (response.data && (response.data as any).generating) {
          // Still building in the background — keep the (warming-up) dashboard and poll again.
          setResults(null);
          timer = setTimeout(() => fetchResults(true), 5000);
        } else {
          setResults(response.data);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching research results:', err);
        setError(t('research.loadResultsError'));
        setResults(null);
      } finally {
        if (!isPoll && !cancelled) setLoading(false);
      }
    };

    if (research.status !== 'DRAFT') {
      fetchResults();
    } else {
      setLoading(false);
      setError(t('research.draftNoResults'));
    }
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [research.id, research.status]);

  // Use fetched results or fallback to basic info
  const data: ResearchData = results || {
    id: research.id,
    name: research.name,
    status: research.status,
    respondent_count: research.respondent_count,
    current_respondents: 0,
    demographics: { age: [], gender: [], geo: [] },
    questions: [],
  };

  const progressPercentage = data.respondent_count > 0
    ? Math.min(100, (data.current_respondents / data.respondent_count) * 100)
    : 0;

  const audienceSections = describeTargeting(data.targeting);

  // Ordered scenario for the Question Results list: interleave creative_display "breaks"
  // (shown creatives, from the research config) between the answered-question result cards,
  // and number questions sequentially (Q1..Qn, breaks not counted).
  const scenarioSteps = (() => {
    const steps: any[] = [];
    const scenario: any[] = Array.isArray((research as any).questions) ? (research as any).questions : [];
    const resById = new Map((data.questions || []).map((q: any) => [q.id, q]));
    let qNum = 0;
    if (scenario.length > 0 && scenario.some((s: any) => s.question_type === 'creative_display')) {
      [...scenario].sort((a, b) => (a.position || 0) - (b.position || 0)).forEach((s: any) => {
        if (s.question_type === 'creative_display') {
          steps.push({ kind: 'creative', step: s });
        } else {
          const res = resById.get(s.id);
          if (res) { qNum += 1; steps.push({ kind: 'question', question: res, number: qNum }); }
        }
      });
    } else {
      (data.questions || []).forEach((q: any) => { qNum += 1; steps.push({ kind: 'question', question: q, number: qNum }); });
    }
    return steps;
  })();

  const handleExport = async (format: 'excel' | 'pdf') => {
    setShowExportMenu(false);
    setExporting(true);

    try {
      const endpoint = format === 'pdf'
        ? `/api/sense/research/${research.id}/export/pdf`
        : `/api/sense/research/${research.id}/export/excel`;

      const response = await api.get(endpoint, { responseType: 'blob' });

      // Get filename from Content-Disposition header or generate default
      const contentDisposition = response.headers['content-disposition'];
      const defaultExt = format === 'pdf' ? 'pdf' : 'xlsx';
      let filename = `Research_${research.id}.${defaultExt}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          filename = match[1];
        }
      }

      // Download the file
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert(t('research.exportError'));
    } finally {
      setExporting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="research-dashboard">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>{t('research.loadingResults')}</p>
        </div>
      </div>
    );
  }

  // Note: while results are still being generated in the background (right after launch),
  // we do NOT block the dashboard — it simply shows the normal "Warming up…" state and the
  // results fill in silently on the next poll.

  // Error state
  if (error && !results) {
    return (
      <div className="research-dashboard">
        <div className="dashboard-header">
          <div className="dashboard-header-left">
            <h2 className="dashboard-title">{research.name}</h2>
            <span className={`dashboard-status status-${research.status.toLowerCase()}`}>
              {researchStatusLabel(research.status)}
            </span>
          </div>
        </div>
        <div className="dashboard-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <circle cx="12" cy="16" r="0.5" fill="currentColor" />
          </svg>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // DIEVO edition gets the redesigned results view (same design as its Share
  // page); Sense keeps this one untouched. The fetch, export and pause/resume
  // handlers stay here — only the presentation differs.
  if (getBranding().edition === 'dievo') {
    return (
      <ResearchDashboardDievo
        data={data as any}
        shareToken={research.share_token}
        aiAnalysisUrl={`/api/sense/research/${research.id}/ai-analysis`}
        aiTranslateUrl={`/api/sense/research/${research.id}/ai-analysis/translate`}
        onPause={onPause}
        onResume={onResume}
        saving={saving}
        exporting={exporting}
        onExportExcel={() => handleExport('excel')}
      />
    );
  }

  return (
    <div className="research-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <h2 className="dashboard-title">{data.name}</h2>
          <span className={`dashboard-status status-${data.status.toLowerCase()}`}>
            {data.status === 'ACTIVE' && <span className="status-pulse" />}
            {researchStatusLabel(data.status)}
          </span>
        </div>
        <div className="dashboard-header-right">
          <ResearchAIAnalysisPanel
            researchStatus={data.status}
            getUrl={`/api/sense/research/${research.id}/ai-analysis`}
            translateUrl={`/api/sense/research/${research.id}/ai-analysis/translate`}
          />

          {data.status === 'ACTIVE' && (
            <button className="dashboard-btn dashboard-btn-warning" onClick={onPause} disabled={saving}>
              {saving ? (
                <span className="btn-spinner" />
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              )}
              {saving ? t('research.pausing') : t('research.pause')}
            </button>
          )}
          {data.status === 'PAUSED' && (
            <button className="dashboard-btn dashboard-btn-success" onClick={onResume} disabled={saving}>
              {saving ? (
                <span className="btn-spinner" />
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              )}
              {saving ? t('research.resuming') : t('research.resume')}
            </button>
          )}
          {/* Hide Export during warmup (no data yet) */}
          {data.current_respondents > 0 && (
            <div className="export-dropdown">
              <button
                className="dashboard-btn dashboard-btn-primary"
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={exporting}
              >
                {exporting ? (
                  <span className="btn-spinner" />
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                )}
                {exporting ? t('research.exporting') : t('research.export')}
                {!exporting && (
                  <svg className="dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                )}
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button onClick={() => handleExport('excel')}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h6v6h6v10H6z"/>
                      <path d="M8 12h8v2H8zm0 4h8v2H8z"/>
                    </svg>
                    {t('research.exportExcel')}
                  </button>
                  <div className="export-menu-divider" />
                  <button onClick={() => {
                    setShowExportMenu(false);
                    window.open(`/research/share/${research.share_token}`, '_blank');
                  }}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                    </svg>
                    {t('research.shareResults')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Progress Section */}
      <div className="dashboard-progress-section">
        <div className="progress-info">
          <div className="progress-numbers">
            <span className="progress-current">{data.current_respondents.toLocaleString(currentLocale())}</span>
            <span className="progress-separator">/</span>
            <span className="progress-target">
              {data.respondent_count.toLocaleString(currentLocale())}
            </span>
            <span className="progress-label">{t('research.respondentsLabel')}</span>
          </div>
          <div className="progress-right">
            {data.progress && !data.progress.is_complete && data.current_respondents === 0 && (
              <span className="progress-warmup">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {t('research.warmingUp')}
              </span>
            )}
            <span className="progress-percentage">{progressPercentage.toFixed(1)}%</span>
          </div>
        </div>
        <div className="progress-bar-container">
          <div
            className={`progress-bar-fill ${data.status === 'ACTIVE' ? 'animate' : ''} ${data.current_respondents === 0 ? 'warmup' : ''}`}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        {/* Start date and estimated completion */}
        {data.progress && !data.progress.is_complete && (
          <div className="progress-dates">
            <div className="progress-date-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>
                Started:{' '}
                <strong>
                  {new Date(data.progress.original_launched_at).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })}
                </strong>
              </span>
            </div>
            <div className="progress-date-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span>
                Est. completion:{' '}
                <strong>
                  {(() => {
                    const launchDate = new Date(data.progress.launched_at);
                    const avgDays = (data.progress.duration_days_min + data.progress.duration_days_max) / 2;
                    const estimatedEnd = new Date(launchDate.getTime() + avgDays * 24 * 60 * 60 * 1000);
                    return estimatedEnd.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
                  })()}
                </strong>
              </span>
            </div>
          </div>
        )}
        {data.progress?.is_complete && (
          <div className="progress-complete">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span>
              {t('research.researchCompleted')}
              {data.progress.original_launched_at && (
                <span className="progress-complete-dates">
                  {' · '}
                  {new Date(data.progress.original_launched_at).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })}
                  {' — '}
                  {(() => {
                    const start = new Date(data.progress.original_launched_at);
                    const avgDays = (data.progress.duration_days_min + data.progress.duration_days_max) / 2;
                    const end = new Date(start.getTime() + avgDays * 24 * 60 * 60 * 1000);
                    return end.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
                  })()}
                </span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* The audience this research was ordered for. Collapsed by default: it is
          the brief, not a result — but on a finished study it is the first thing
          anyone asks when a number looks surprising. */}
      {audienceSections.length > 0 && (
        <div className={`audience-brief ${showAudience ? 'expanded' : ''}`}>
          <button className="audience-brief-header" onClick={() => setShowAudience((v) => !v)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
            </svg>
            <span className="audience-brief-title">{t('research.audienceSettings')}</span>
            <span className="audience-brief-summary">
              {audienceSections.map((s) => `${t(s.labelKey)}: ${s.values.length}`).join(' · ')}
            </span>
            <svg className="audience-brief-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {showAudience && (
            <div className="audience-brief-body">
              {audienceSections.map((section) => (
                <div key={section.labelKey} className="audience-brief-row">
                  <div className="audience-brief-label">{t(section.labelKey)}</div>
                  <div className="audience-brief-values">
                    {section.values.map((v) => (
                      <span key={v} className="audience-brief-chip">{v}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Demographics Section */}
      <div className="dashboard-section">
        <h3 className="section-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          {t('research.passport')}
        </h3>
        {data.current_respondents === 0 ? (
          <div className="warmup-placeholder">
            <div className="warmup-placeholder-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <h4>{t('research.collecting')}</h4>
            <p>Demographics data will appear here once the first responses are collected.</p>
          </div>
        ) : (
          <div className="demographics-grid">
            <DonutChart
              title={t('research.ageDistribution')}
              data={data.demographics.age.map((item, index) => ({
                label: item.range,
                value: item.count,
                percentage: item.percentage,
                color: AGE_COLORS[index % AGE_COLORS.length],
              }))}
            />
            <DonutChart
              title={t('research.genderDistribution')}
              data={data.demographics.gender.map((item, index) => ({
                label: targetingLabel(item.label),
                value: item.count,
                percentage: item.percentage,
                color: GENDER_COLORS[index % GENDER_COLORS.length],
              }))}
            />
            <DonutChart
              title={t('research.geography')}
              maxLegendItems={5}
              data={(data.demographics.geo || []).map((item, index) => ({
                label: item.region,
                value: item.count,
                percentage: item.percentage,
                color: GEO_COLORS[index % GEO_COLORS.length],
              }))}
            />
          </div>
        )}
      </div>

      {/* Questions Results Section */}
      <div className="dashboard-section">
        <h3 className="section-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <circle cx="12" cy="17" r="0.5" fill="currentColor" />
          </svg>
          {t('research.questionResults')}
        </h3>
        {data.current_respondents === 0 ? (
          <div className="warmup-placeholder">
            <div className="warmup-placeholder-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
            </div>
            <h4>{t('research.waitingResponses')}</h4>
            <p>Question results will be displayed once participants start answering the survey.</p>
          </div>
        ) : (
        <div className="questions-results">
          {scenarioSteps.map((__item: any, __idx: number) => __item.kind === 'creative' ? (
            <div key={`c${__idx}`} className="question-result-card creative-break-card" style={{ padding: 16, textAlign: 'center' }}>
              {__item.step.media_url && (
                __item.step.media_type === 'video' ? (
                  <video src={imgUrl(__item.step.media_url)} controls
                    style={{ display: 'block', maxWidth: '100%', maxHeight: 320, margin: '0 auto', borderRadius: 10, background: '#000' }} />
                ) : (
                  <img src={imgUrl(__item.step.media_url)} alt=""
                    style={{ display: 'block', maxWidth: '100%', maxHeight: 320, margin: '0 auto', borderRadius: 10 }} />
                )
              )}
              {__item.step.question_text && (
                <div style={{ fontSize: 15, color: 'var(--text-secondary, #868e96)', marginTop: 12 }}>
                  {__item.step.question_text}
                </div>
              )}
            </div>
          ) : (() => {
            const question: QuestionResult = __item.question;
            const qIndex: number = __item.number - 1;
            return (
            <div
              key={question.id}
              className={`question-result-card ${expandedQuestion === question.id ? 'expanded' : ''}`}
            >
              <div
                className="question-result-header"
                onClick={() => setExpandedQuestion(expandedQuestion === question.id ? null : question.id)}
              >
                <div className="question-number">Q{qIndex + 1}</div>
                <div className="question-text">{question.question_text}</div>
                <svg className="expand-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>

              <div className="question-result-content">
                <div className="question-charts-row">
                  {/* Chart section - Pie for single_choice, Bar for multiple_choice */}
                  <div className={`question-pie-section ${question.question_type === 'multiple_choice' ? 'multiple-choice-section' : ''}`}>
                    {question.question_type === 'multiple_choice' ? (
                      <>
                        <div className="multiple-choice-label">{t('research.multipleAnswers')}</div>
                        <HorizontalBarChart
                          data={question.answers.map((answer, index) => ({
                            label: answer.answer_text,
                            count: answer.count,
                            percentage: answer.percentage,
                            color: ANSWER_COLORS[index % ANSWER_COLORS.length],
                          }))}
                        />
                      </>
                    ) : (
                      <>
                        <PieChart
                          data={question.answers.map((answer, index) => ({
                            label: answer.answer_text,
                            count: answer.count,
                            percentage: answer.percentage,
                            color: ANSWER_COLORS[index % ANSWER_COLORS.length],
                            image: question.question_type === 'creative_test' ? answer.answer_image : null,
                          }))}
                        />
                        <div className={`question-legend ${question.question_type === 'creative_test' ? 'creative-legend' : ''}`}>
                          {question.answers.map((answer, index) => (
                            <div key={answer.id} className="question-legend-item">
                              <span
                                className="legend-color"
                                style={{ background: ANSWER_COLORS[index % ANSWER_COLORS.length] }}
                              />
                              {question.question_type === 'creative_test' && answer.answer_image ? (
                                <img className="legend-thumb" src={imgUrl(answer.answer_image)} alt={`Option ${index + 1}`} />
                              ) : (
                                <span className="legend-text">{answer.answer_text}</span>
                              )}
                              <span className="legend-value">{answer.count.toLocaleString(currentLocale())} ({answer.percentage}%)</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Stacked bar chart */}
                  <div className="question-breakdown-section">
                    <h5 className="breakdown-title">{t('research.breakdownDemographics')}</h5>
                    <StackedBarChart
                      breakdown={question.demographic_breakdown}
                      answers={question.answers}
                    />
                  </div>
                </div>
              </div>
            </div>
            );
          })())}
        </div>
        )}
      </div>

    </div>
  );
};

export default ResearchDashboard;
